"use client";

import { useEffect, useRef } from "react";
import type { TranscriptEntry } from "@/lib/geminiSession";

interface TranscriptDisplayProps {
  entries: TranscriptEntry[];
  agentName: string;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString("sl-SI", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function TranscriptDisplay({ entries, agentName }: TranscriptDisplayProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest entry
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500 text-sm select-none">
        Transkript bo prikazan tukaj…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 overflow-y-auto h-full px-1 py-2">
      {entries.map((entry, i) => {
        const isAgent = entry.role === "agent";
        return (
          <div
            key={i}
            className={`flex flex-col gap-1 max-w-[88%] ${
              isAgent ? "self-start" : "self-end items-end"
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isAgent ? "text-blue-400" : "text-emerald-400"
                }`}
              >
                {isAgent ? agentName : "Vi"}
              </span>
              <span className="text-xs text-slate-600">{formatTime(entry.timestamp)}</span>
            </div>
            <div
              className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                isAgent
                  ? "bg-slate-800 text-slate-100 rounded-tl-sm"
                  : "bg-emerald-900/60 text-emerald-50 rounded-tr-sm"
              }`}
            >
              {entry.text}
            </div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
